/* global google */
import Map from "@churchofjesuschrist/maps-google-map";
import MapsService, {
  MapProviderType
} from "@churchofjesuschrist/maps-service";
import { configureMapsService } from "../../../../utilities/maps-service-config";
import Marker, { IconType } from "@churchofjesuschrist/maps-google-marker";
import { LocationType } from "@churchofjesuschrist/maps-google-clusters";

export default class PinDropMap {
  constructor(element, initialCoordinates, config = {}) {
    this.element = element;
    this.map = null;
    this.marker = null; // Draggable pin marker (red)
    this.locationMarker = null; // Initial location marker (blue, non-draggable)
    this.onPinDropped = null; // Callback function
    this.onGeolocateError = null; // Callback for map geolocate control failures
    this.precision = config.precision || "address"; // Default to address-level
    this.initialZoom = config.initialZoom;
    this.initialAddress = config.initialAddress; // Store original address if provided
    this.mapErrorMessage = config.mapErrorMessage;
    this.mapsLanguage =
      config.language ||
      window.PUBLIC_ENV?.lang ||
      document.documentElement.lang ||
      "en";
    this.mapsIsProd =
      config.production !== undefined
        ? config.production
        : window.PUBLIC_ENV?.LANE === "prod";

    this.ready = this.initMap(initialCoordinates);
  }

  // Loads Google Maps and initializes the pin-drop map at the starting point.
  async initMap(coordinates) {
    if (!this.element) {
      this.failMap("Map container element not found");
      return;
    }

    const googleCoords = PinDropMap.toGoogleCoordinates(coordinates);
    if (!googleCoords) {
      this.failMap("Valid initial map coordinates are required", null);
      return;
    }

    // Configure MapsService using singleton pattern
    if (!configureMapsService()) {
      this.failMap("Failed to configure MapsService");
      return;
    }

    // Load Google Maps with geometry library
    const loadMapsOptions = {
      libraries: ["geometry"],
      language: this.mapsLanguage
    };

    try {
      await MapsService.loadMaps(MapProviderType.GOOGLE, loadMapsOptions);

      if (typeof Map !== "function") {
        this.failMap(
          "Map constructor is not available. Google Maps may not have loaded properly."
        );
        return;
      }

      const zoom =
        this.initialZoom || PinDropMap.getZoomForPrecision(this.precision);

      // Create the map with optimal settings for pin-drop (use googleCoords format)
      const mapOptions = {
        center: googleCoords,
        zoom: zoom,
        draggable: true, // Allow map panning
        gestureHandling: "greedy", // Allow one-finger pan on mobile, no Ctrl required on desktop
        // Map controls - disable everything except zoom
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: 7 // RIGHT_TOP position
        },
        // Enable the geolocate button for user's current location
        geolocateControl: true,
        legalControl: true
      };

      this.map = new Map(this.element, mapOptions);

      // Wait a moment for map to initialize
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Start with a draggable pin at the initial location.
      this.placePin(googleCoords);

      this.setupClickHandler();
      this.setupGeolocateHandler();
    } catch (error) {
      this.failMap("Unable to initialize pin drop map", error);
    }
  }

  // Shows the map failure UI and logs the underlying initialization problem.
  failMap(message, cause) {
    if (this.element) {
      PinDropMap.renderMapLoadError(this.element, this.mapErrorMessage);
      this.element.dispatchEvent(
        new CustomEvent("pin-drop-map-error", {
          bubbles: true,
          detail: {
            message,
            error: PinDropMap.serializeError(cause)
          }
        })
      );
    }

    PinDropMap.logMapError(message, cause, {
      precision: this.precision,
      language: this.mapsLanguage,
      production: this.mapsIsProd
    });
  }

  // Logs map errors in the structured format expected by CUCL logging.
  static logMapError(message, cause, context = {}) {
    console.error(
      JSON.stringify({
        "cuclog-error": "Pin Drop Map Error",
        message,
        error: PinDropMap.serializeError(cause),
        ...context
      })
    );
  }

  // Converts thrown values into JSON-safe error details for logging/events.
  static serializeError(error) {
    if (!error) {
      return null;
    }

    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack
      };
    }

    try {
      return JSON.parse(JSON.stringify(error));
    } catch (serializationError) {
      return String(error);
    }
  }

  // Normalizes supported coordinate shapes into Google Maps lat/lng objects.
  static toGoogleCoordinates(coordinates) {
    const lat = PinDropMap.toFiniteNumber(
      coordinates?.latitude ?? coordinates?.lat
    );
    const lng = PinDropMap.toFiniteNumber(
      coordinates?.longitude ?? coordinates?.lng
    );

    if (lat === null || lng === null) {
      return null;
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }

    return { lat, lng };
  }

  // Converts coordinate-like values into finite numbers or null.
  static toFiniteNumber(value) {
    if (value === "" || value === null || value === undefined) {
      return null;
    }

    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  // Renders a simple fallback panel when the map cannot be displayed.
  static renderMapLoadError(element, message) {
    element.replaceChildren();

    const wrapper = document.createElement("div");
    wrapper.style.display = "flex";
    wrapper.style.alignItems = "center";
    wrapper.style.justifyContent = "center";
    wrapper.style.height = "100%";
    wrapper.style.background = "#f5f5f5";
    wrapper.style.color = "#666";
    wrapper.style.textAlign = "center";
    wrapper.style.padding = "20px";
    wrapper.textContent =
      PinDropMap.firstString(message) ||
      "Map failed to load. Please refresh the page.";

    element.appendChild(wrapper);
  }

  // Lets map clicks move the draggable pin and reverse-geocode the new point.
  setupClickHandler() {
    if (!this.map) return;

    this.map.addListener("click", (event) => {
      const coordinates = {
        latitude: event.latLng.lat(),
        longitude: event.latLng.lng()
      };

      // Convert to Google Maps format for placePin
      const googleCoords = {
        lat: coordinates.latitude,
        lng: coordinates.longitude
      };

      // User manually clicked - clear initial address so validation uses new location
      this.initialAddress = null;

      // Remove location marker if it exists
      if (this.locationMarker) {
        this.locationMarker.setMap(null);
        this.locationMarker = null;
      }

      // Place draggable pin and reverse geocode
      this.placePin(googleCoords);
      this.reverseGeocodePosition(coordinates);
    });
  }

  // Connects the map geolocate control to pin placement and error handling.
  setupGeolocateHandler() {
    if (!this.map) return;

    this.map.addListener("geolocate_success", (coords) => {
      const coordinates = {
        latitude: coords?.latitude,
        longitude: coords?.longitude
      };

      if (
        typeof coordinates.latitude !== "number" ||
        typeof coordinates.longitude !== "number"
      ) {
        return;
      }

      const googleCoords = {
        lat: coordinates.latitude,
        lng: coordinates.longitude
      };

      // User chose the map geolocate control, so use the resolved location.
      this.initialAddress = null;

      if (this.locationMarker) {
        this.locationMarker.setMap(null);
        this.locationMarker = null;
      }

      this.placePin(googleCoords);
      this.reverseGeocodePosition(coordinates);
    });

    this.map.addListener("geolocate_error", (error) => {
      this.onGeolocateError?.(error);
    });
  }

  // Places the non-draggable starting-location marker.
  placeLocationMarker(coordinates) {
    // Remove existing location marker
    if (this.locationMarker) {
      this.locationMarker.setMap(null);
    }

    const googleCoords = PinDropMap.toGoogleCoordinates(coordinates);
    if (!googleCoords) {
      PinDropMap.logMapError(
        "Unable to place pin drop location marker without valid coordinates"
      );
      return;
    }

    try {
      // Create non-draggable location marker (golden/amber color like church product map)
      this.locationMarker = new Marker({
        icon: {
          type: IconType.PIN,
          layer: LocationType.LOCALITY
        },
        position: googleCoords,
        map: this.map,
        draggable: false // Not draggable - user must click to drop pin
      });

      // Center map on the marker
      this.map.setCenter(googleCoords);
    } catch (error) {
      PinDropMap.logMapError("Unable to place pin drop location marker", error);
    }
  }

  // Places or replaces the draggable pin marker.
  placePin(coordinates) {
    // Remove existing marker
    if (this.marker) {
      this.marker.setMap(null);
    }

    const googleCoords = PinDropMap.toGoogleCoordinates(coordinates);
    if (!googleCoords) {
      PinDropMap.logMapError(
        "Unable to place pin drop marker without valid coordinates"
      );
      return;
    }

    try {
      // Create new marker at the position
      this.marker = new Marker({
        icon: {
          type: IconType.PIN,
          color: "#D50032", // Church red color
          scale: 1.2
        },
        position: googleCoords,
        map: this.map,
        draggable: true // Allow users to drag the pin to fine-tune position
        // Removed deprecated animation property
      });

      // Add dragend listener to update address when user drags the pin
      this.marker.addListener("dragend", (event) => {
        const coordinates = {
          latitude: event.latLng.lat(),
          longitude: event.latLng.lng()
        };
        // User manually dragged pin - clear initial address so validation uses new location
        this.initialAddress = null;
        this.reverseGeocodePosition(coordinates);
      });

      // Center map on the new pin
      this.map.setCenter(googleCoords);
    } catch (error) {
      PinDropMap.logMapError("Unable to place pin drop marker", error);
    }
  }

  // Applies a numeric zoom level when the map API is ready.
  setMapZoom(zoom) {
    if (!this.map || typeof this.map.setZoom !== "function") {
      return;
    }

    if (zoom === null || zoom === undefined) {
      return;
    }

    const zoomLevel = Number(zoom);
    if (!Number.isFinite(zoomLevel)) {
      return;
    }

    this.map.setZoom(zoomLevel);
  }

  // Sets zoom from the address precision inferred from a geocode result.
  setZoomForGeocodeResult(geocodeResult) {
    this.setMapZoom(PinDropMap.getZoomForGeocodeResult(geocodeResult));
  }

  // Resolves coordinates back to an address after a click, drag, or geolocate.
  async reverseGeocodePosition(coordinates) {
    try {
      const results = await MapsService.reverseGeocode(coordinates, {
        providers: [MapProviderType.GOOGLE],
        limit: 1,
        language: this.mapsLanguage,
        unconverted: false
      });

      if (results && results.length > 0 && results[0]) {
        this.useGeocodeResult(results[0], coordinates);
      } else {
        this.publishFallbackCoordinates(coordinates);
      }
    } catch (error) {
      PinDropMap.logMapError(
        "Unable to reverse geocode pin drop position",
        error
      );
      this.publishFallbackCoordinates(coordinates);
    }
  }

  // Publishes the address, coordinates, validity, and country for a geocode result.
  useGeocodeResult(geocodeResult, coordinates) {
    const isValidAddressLocation =
      PinDropMap.validateAddressLocation(geocodeResult);
    const looksLikeFullAddress =
      this.initialAddress &&
      (this.initialAddress.match(/\d/) || this.initialAddress.includes(","));
    const isValid =
      isValidAddressLocation && (!this.initialAddress || looksLikeFullAddress);
    const addressToUse =
      this.initialAddress && isValid
        ? this.initialAddress
        : PinDropMap.formatAddress(geocodeResult);

    if (PinDropMap.isUsableAddress(addressToUse)) {
      this.onPinDropped?.(
        addressToUse,
        coordinates,
        isValid,
        PinDropMap.getGeocodeResultCountry(geocodeResult)
      );
    } else {
      this.publishFallbackCoordinates(coordinates);
    }
  }

  // Publishes coordinates as an invalid fallback when no usable address is found.
  publishFallbackCoordinates(coordinates) {
    const lat = coordinates.latitude || coordinates.lat;
    const lng = coordinates.longitude || coordinates.lng;
    const fallbackAddress =
      typeof lat === "number" && typeof lng === "number"
        ? `${lat.toFixed(6)}, ${lng.toFixed(6)}`
        : "";

    this.onPinDropped?.(fallbackAddress, coordinates, false, null);
  }

  // Checks whether a formatted address is safe to show in the search field.
  static isUsableAddress(address) {
    return (
      address &&
      typeof address === "string" &&
      address.trim() &&
      address !== "Address not found" &&
      !address.includes("NaN")
    );
  }

  // Determines whether a geocode result is precise enough to save as an address.
  static validateAddressLocation(geocodeResult) {
    const street1Value =
      geocodeResult.street1 || geocodeResult.address?.street1;
    const components =
      geocodeResult.address_components || geocodeResult.components || [];
    const hasStreetNumber = components.some((component) =>
      component.types?.includes("street_number")
    );
    const hasRoute = components.some((component) =>
      component.types?.includes("route")
    );
    const hasStreetAddress = Boolean(
      (street1Value && street1Value.trim()) || (hasStreetNumber && hasRoute)
    );
    const match = geocodeResult.match || {};
    const matchLevel = match.level; // STREET, BUILDING, ROOFTOP, etc.
    const matchAccuracy = match.accuracy; // ROOFTOP, RANGE_INTERPOLATED, etc.
    const locationType = PinDropMap.firstString(
      matchAccuracy ||
        geocodeResult.locationType ||
        geocodeResult.location_type ||
        geocodeResult.geometry?.location_type ||
        geocodeResult.accuracy
    )?.toUpperCase();
    const hasGoodPrecision =
      locationType === "ROOFTOP" || locationType === "RANGE_INTERPOLATED";
    const hasBadPrecision =
      locationType === "GEOMETRIC_CENTER" || locationType === "APPROXIMATE";
    const types = geocodeResult.types || [];
    const typeField = geocodeResult.type; // Single type field from MapsService
    const validMatchLevels = ["STREET", "BUILDING", "ROOFTOP", "PREMISE"];
    const validMatchAccuracy = ["ROOFTOP", "RANGE_INTERPOLATED"];
    const hasValidMatchLevel =
      (matchLevel &&
        validMatchLevels.some((level) =>
          matchLevel.toUpperCase().includes(level)
        )) ||
      (matchAccuracy &&
        validMatchAccuracy.includes(matchAccuracy.toUpperCase())) ||
      hasGoodPrecision;
    const largeAreaTypeValues = [
      "COUNTRY",
      "ADMINISTRATIVE_AREA",
      "LOCALITY",
      "POLITICAL"
    ];
    const isLargeAreaType =
      typeField &&
      largeAreaTypeValues.some((largeType) =>
        typeField.toUpperCase().includes(largeType)
      );
    const isActuallyLargeArea = isLargeAreaType && !hasValidMatchLevel;
    const hasInvalidTypes = types.some(
      (type) =>
        type === "natural_feature" ||
        type === "park" ||
        (type === "point_of_interest" && !hasStreetAddress) // POI without street is invalid
    );
    const hasOnlyLargeAreaTypes =
      types.length > 0 &&
      types.every(
        (type) =>
          type === "country" ||
          type === "administrative_area_level_1" ||
          type === "administrative_area_level_2" ||
          type === "locality" ||
          type === "political"
      );
    const hasEmptyTypes = types.length === 0;

    return (
      hasStreetAddress &&
      (hasGoodPrecision || !hasBadPrecision) &&
      !hasInvalidTypes &&
      !hasOnlyLargeAreaTypes &&
      (hasValidMatchLevel || (!hasEmptyTypes && !isActuallyLargeArea))
    );
  }

  // Maps fallback precision labels to default map zoom levels.
  static getZoomForPrecision(precision) {
    const zoomLevels = {
      address: 17,
      geolocation: 15,
      ip: 12,
      country: 3
    };

    return zoomLevels[precision] || 12;
  }

  // Maps a geocode result's inferred level to a map zoom level.
  static getZoomForGeocodeResult(geocodeResult) {
    const level = PinDropMap.getGeocodeResultLevel(geocodeResult);
    const zoomLevels = {
      country: 3,
      state: 6,
      city: 11,
      street: 12,
      rooftop: 17
    };

    return zoomLevels[level] || null;
  }

  // Infers country, state, city, street, or rooftop precision from geocode data.
  static getGeocodeResultLevel(geocodeResult) {
    const match = geocodeResult?.match || {};
    const address = geocodeResult?.address || {};
    const formattedAddressValue = PinDropMap.firstString(
      geocodeResult?.formatted,
      typeof address === "string" ? address : null,
      address?.formatted,
      geocodeResult?.formatted_address,
      geocodeResult?.formattedAddress
    );
    const components =
      geocodeResult?.address_components || geocodeResult?.components || [];
    const street1Value = PinDropMap.firstString(
      geocodeResult?.street1,
      address?.street1
    );
    const types = (geocodeResult?.types || []).map((type) =>
      String(type).toLowerCase()
    );
    const typeField = PinDropMap.firstString(
      geocodeResult?.type
    )?.toLowerCase();
    const matchLevel = PinDropMap.firstString(match.level)?.toLowerCase();
    const locationType = PinDropMap.firstString(
      match.accuracy,
      geocodeResult?.locationType,
      geocodeResult?.location_type,
      geocodeResult?.geometry?.location_type,
      geocodeResult?.accuracy
    )?.toLowerCase();
    const componentTypes = components.flatMap((component) =>
      (component.types || []).map((type) => String(type).toLowerCase())
    );
    const allTypes = new Set([
      ...types,
      ...componentTypes,
      ...(typeField ? [typeField] : []),
      ...(matchLevel ? [matchLevel] : [])
    ]);
    const primaryTypes = new Set([
      ...types,
      ...(typeField ? [typeField] : []),
      ...(matchLevel ? [matchLevel] : [])
    ]);
    const hasType = (...values) => values.some((value) => allTypes.has(value));
    const hasPrimaryType = (...values) =>
      values.some((value) => primaryTypes.has(value));
    const hasStreetNumber = hasType("street_number");
    const hasRoute = hasType("route");
    const hasStreetAddress = Boolean(
      street1Value || (hasStreetNumber && hasRoute)
    );
    const hasFormattedStreetAddress = PinDropMap.looksLikeStreetAddress(
      formattedAddressValue
    );

    if (
      locationType === "rooftop" ||
      hasType("rooftop", "building", "premise") ||
      (hasStreetAddress && matchLevel === "building") ||
      hasFormattedStreetAddress
    ) {
      return "rooftop";
    }

    if (
      locationType === "range_interpolated" ||
      hasStreetAddress ||
      hasType("street", "street_address", "route", "intersection")
    ) {
      return "street";
    }

    if (hasPrimaryType("country")) {
      return "country";
    }

    if (
      hasPrimaryType(
        "locality",
        "postal_town",
        "sublocality",
        "sublocality_level_1"
      )
    ) {
      return "city";
    }

    if (
      hasPrimaryType(
        "administrative_area",
        "administrative_area_level_1",
        "administrative_area_level_2",
        "state"
      )
    ) {
      return "state";
    }

    if (hasType("country")) {
      return "country";
    }

    return null;
  }

  // Detects formatted addresses that start with a street number.
  static looksLikeStreetAddress(address) {
    return /^\s*\d+[a-z]?\s+\S+/i.test(String(address || ""));
  }

  // Checks whether a geocode result belongs to the selected address country.
  static geocodeResultMatchesCountry(
    geocodeResult,
    countryAlpha3,
    countryCode,
    countryName
  ) {
    if (!countryAlpha3 && !countryCode && !countryName) {
      return true;
    }

    const resultCountry = PinDropMap.getGeocodeResultCountry(geocodeResult);

    if (
      !resultCountry.alpha3 &&
      !resultCountry.code &&
      !resultCountry.name &&
      !resultCountry.alpha2
    ) {
      return true;
    }

    if (
      countryName &&
      resultCountry.name &&
      countryName.toUpperCase() === resultCountry.name.toUpperCase()
    ) {
      return true;
    }

    if (
      countryAlpha3 &&
      resultCountry.alpha3 &&
      countryAlpha3.toUpperCase() === resultCountry.alpha3.toUpperCase()
    ) {
      return true;
    }

    return (
      countryCode &&
      resultCountry.code &&
      String(countryCode) === String(resultCountry.code)
    );
  }

  // Checks whether the user explicitly typed the result country at query end.
  static queryIndicatesCountry(query, country) {
    const countryValues = [
      country?.name,
      country?.alpha2,
      country?.alpha3
    ].filter(Boolean);
    const normalizedQuery = String(query || "").toLowerCase();

    return countryValues.some((value) => {
      const normalizedValue = String(value).toLowerCase();
      const escapedValue = normalizedValue.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&"
      );
      const countryPattern = new RegExp(
        `(^|[\\s,])${escapedValue}\\s*[.,]?\\s*$`,
        "i"
      );

      return countryPattern.test(normalizedQuery);
    });
  }

  // Extracts normalized country fields from service-specific geocode shapes.
  static getGeocodeResultCountry(geocodeResult) {
    const address = geocodeResult?.address || {};
    const components =
      geocodeResult?.address_components || geocodeResult?.components || [];
    const countryComponent = components.find((component) =>
      component.types?.includes("country")
    );

    return {
      name: PinDropMap.firstString(
        geocodeResult?.country,
        address?.country,
        countryComponent?.long_name
      ),
      alpha3: PinDropMap.firstString(
        geocodeResult?.countryCode3,
        geocodeResult?.country_code3,
        address?.countryCode3,
        address?.country_code3
      ),
      alpha2: PinDropMap.firstString(
        geocodeResult?.countryCode2,
        geocodeResult?.country_code2,
        address?.countryCode2,
        address?.country_code2,
        countryComponent?.short_name
      ),
      code: PinDropMap.firstString(
        geocodeResult?.countryId,
        geocodeResult?.countryID,
        geocodeResult?.countryCode,
        address?.countryId,
        address?.countryID,
        address?.countryCode
      )
    };
  }

  // Builds the best display address available from a geocode result.
  static formatAddress(geocodeResult) {
    // Try to format a readable address from the geocode result
    if (typeof geocodeResult === "string") {
      return geocodeResult;
    }

    // Check if the object is empty or null
    if (!geocodeResult || Object.keys(geocodeResult).length === 0) {
      return null;
    }

    const addressObj = geocodeResult.address;
    const formattedAddress = PinDropMap.firstString(
      geocodeResult.formatted,
      addressObj?.formatted,
      geocodeResult.formatted_address,
      geocodeResult.formattedAddress
    );

    if (formattedAddress) {
      return formattedAddress;
    }

    // Try direct properties first
    if (geocodeResult.street1 || geocodeResult.city || geocodeResult.state) {
      const parts = [
        geocodeResult.street1,
        geocodeResult.city,
        geocodeResult.state,
        geocodeResult.zipCode || geocodeResult.postalCode
      ].filter(Boolean);

      if (parts.length > 0) {
        return parts.join(" ");
      }
    }
    // Try nested address object
    else if (
      addressObj &&
      (addressObj.street1 || addressObj.city || addressObj.state)
    ) {
      const parts = [
        addressObj.street1,
        addressObj.city,
        addressObj.state,
        addressObj.zipCode || addressObj.postalCode
      ].filter(Boolean);

      if (parts.length > 0) {
        return parts.join(" ");
      }
    }

    // Handle Google Maps geocode result format (fallback)
    const formatted = geocodeResult.address;

    // If it's a string, use it
    if (formatted && typeof formatted === "string" && formatted.trim()) {
      return formatted.trim();
    }
    // If it's an object (like the address object), try to build from its properties
    else if (
      formatted &&
      typeof formatted === "object" &&
      (formatted.street1 || formatted.city || formatted.state)
    ) {
      const parts = [
        formatted.street1,
        formatted.city,
        formatted.state,
        formatted.zipCode || formatted.postalCode
      ].filter(Boolean);

      if (parts.length > 0) {
        return parts.join(" ");
      }
    }

    // Try to build address from components
    const components =
      geocodeResult.address_components || geocodeResult.components;
    if (components && Array.isArray(components)) {
      const getComponent = (type, key = "long_name") =>
        components.find((component) => component.types?.includes(type))?.[key];

      const streetNumber = getComponent("street_number");
      const route = getComponent("route");
      const locality = getComponent("locality");
      const region = getComponent("administrative_area_level_1", "short_name");
      const postalCode = getComponent("postal_code");

      const parts = [
        [streetNumber, route].filter(Boolean).join(" "),
        locality,
        region,
        postalCode
      ].filter(Boolean);

      if (parts.length > 0) {
        return parts.join(" ");
      }
    }

    // Last resort - check for coordinates to confirm we have location data
    const coords =
      geocodeResult.coordinates || geocodeResult.geometry?.location;
    if (coords) {
      const lat =
        coords.latitude ||
        coords.lat ||
        (typeof coords.lat === "function" ? coords.lat() : null);
      const lng =
        coords.longitude ||
        coords.lng ||
        (typeof coords.lng === "function" ? coords.lng() : null);
      if (lat && lng) {
        return null; // Return null so caller can use coordinate fallback
      }
    }

    return null; // Return null instead of 'Address not found' so caller handles fallback
  }

  // Returns the first non-empty trimmed string from a list of candidate values.
  static firstString(...values) {
    return values
      .find((value) => typeof value === "string" && value.trim())
      ?.trim();
  }

  // Removes map and marker listeners before the modal is closed.
  destroy() {
    if (this.marker) {
      // Clean up marker event listeners
      if (typeof google !== "undefined" && google.maps) {
        google.maps.event.clearInstanceListeners(this.marker);
      }
      this.marker.setMap(null);
      this.marker = null;
    }
    if (this.locationMarker) {
      // Clean up location marker
      if (typeof google !== "undefined" && google.maps) {
        google.maps.event.clearInstanceListeners(this.locationMarker);
      }
      this.locationMarker.setMap(null);
      this.locationMarker = null;
    }
    if (this.map) {
      // Clean up map event listeners
      if (typeof google !== "undefined" && google.maps) {
        google.maps.event.clearListeners(this.map, "click");
        google.maps.event.clearListeners(this.map, "geolocate_success");
        google.maps.event.clearListeners(this.map, "geolocate_error");
      }
      this.map = null;
    }
    this.onPinDropped = null;
    this.onGeolocateError = null;
  }
}
